import React, { useState, useEffect } from 'react'

export default function InventoryReportsTable({ initialReports, warehouseFilter, reportTypeFilter }) {
  const [reports, setReports] = useState(initialReports || [])
  const [isPolling, setIsPolling] = useState(true)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (initialReports && initialReports.length > 0) {
      setReports(initialReports)
      const hasProcessing = initialReports.some(r =>
        r.status === 'pending' || r.status === 'processing'
      )
      if (!hasProcessing) {
        setIsPolling(false)
      }
    }
  }, [initialReports])

  useEffect(() => {
    if (!isPolling) return

    const interval = setInterval(() => {
      fetchReports()
    }, 3000)

    return () => clearInterval(interval)
  }, [isPolling, warehouseFilter, reportTypeFilter])

  const fetchReports = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()

      if (warehouseFilter) params.append('warehouse_id', warehouseFilter)
      if (reportTypeFilter) params.append('report_type', reportTypeFilter)

      const url = `/inventory_reports.json?${params.toString()}`

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      const reportsList = data.reports || []
      setReports(reportsList)

      const hasProcessing = reportsList.some(r =>
        r.status === 'pending' || r.status === 'processing'
      )
      if (!hasProcessing) {
        setIsPolling(false)
      }
    } catch (error) {
      console.error('Error fetching reports:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: 'badge-warning', display: 'Pending', icon: null },
      processing: { class: 'badge-info', display: 'Processing', icon: 'fas fa-spinner fa-spin mr-1' },
      completed: { class: 'badge-success', display: 'Completed', icon: null },
      failed: { class: 'badge-danger', display: 'Failed', icon: null }
    }
    return statusMap[status] || { class: 'badge-secondary', display: status, icon: null }
  }

  const getReportTypeBadgeClass = (reportType) => {
    const classes = {
      snapshot: 'badge-primary',
      movement: 'badge-info',
      low_stock: 'badge-warning',
      purchase_analysis: 'badge-success',
      usage_analysis: 'badge-danger',
      variance: 'badge-secondary',
      valuation: 'badge-dark'
    }
    return classes[reportType] || 'badge-secondary'
  }

  const formatReportType = (reportType) => {
    return reportType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  const getActionButtons = (report) => {
    const viewBtn = (
      <a
        key={`view-${report.id}`}
        href={`/inventory_reports/${report.id}`}
        className="btn btn-outline-blue btn-sm fas fa-eye"
        title="View"
        data-toggle="tooltip"
      />
    )

    const downloadBtn = (
      <a
        key={`download-${report.id}`}
        href={`/inventory_reports/${report.id}.csv`}
        className="btn btn-outline-blue btn-sm fas fa-download"
        title="Download CSV"
        data-toggle="tooltip"
      />
    )

    const deleteBtn = (
      <a
        key={`delete-${report.id}`}
        href={`/inventory_reports/${report.id}`}
        data-method="delete"
        className="btn btn-outline-blue btn-sm fas fa-trash"
        title="Delete"
        data-toggle="tooltip"
        data-confirm="Are you sure you want to delete this report?"
      />
    )

    if (report.status === 'completed') {
      return [viewBtn, downloadBtn, deleteBtn]
    }
    if (report.status === 'failed' || report.status === 'pending') {
      return [deleteBtn]
    }
    return (
      <span className="text-muted small">
        <i className="fas fa-spinner fa-spin"></i>
      </span>
    )
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const formatDateRange = (report) => {
    // For snapshot and low_stock reports, show only the report date
    if (report.report_type === 'snapshot' || report.report_type === 'low_stock') {
      return report.start_date
    }
    // For range-based reports, show the date range
    return `${report.start_date} - ${report.end_date}`
  }

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }


  return (
    <div className="table-container">
      {loading && (
        <div className="alert alert-info mb-3">
          <i className="fas fa-sync fa-spin mr-2"></i>
          Updating report statuses...
        </div>
      )}

      {reports.length === 0 ? (
        <div className="text-center py-5">
          <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
          <p className="text-muted">No reports found</p>
          <a href="/inventory_reports/new" className="btn btn-success">
            <i className="fas fa-plus mr-1"></i>
            Generate First Report
          </a>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover inventory-reports-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Report Type</th>
                <th scope="col">Warehouse</th>
                <th scope="col">Date Range</th>
                <th scope="col">Status</th>
                <th scope="col">Generated By</th>
                <th scope="col">Created</th>
                <th className="sm-th" scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => {
                const statusBadge = getStatusBadge(report.status)
                const reportTypeBadge = getReportTypeBadgeClass(report.report_type)

                return (
                  <tr key={report.id}>
                    <td scope="row">{report.id}</td>
                    <td>
                      <span className={`badge ${reportTypeBadge}`}>
                        {formatReportType(report.report_type)}
                      </span>
                    </td>
                    <td>
                      {report.warehouse?.name ? (
                        report.warehouse.name
                      ) : (
                        <span className="text-primary">All Warehouses</span>
                      )}
                    </td>
                    <td>
                      {formatDateRange(report)}
                    </td>
                    <td>
                      <span className={`badge ${statusBadge.class}`}>
                        {statusBadge.icon && (
                          <i className={statusBadge.icon}></i>
                        )}
                        {statusBadge.display}
                      </span>
                    </td>
                    <td>
                      {report.generated_by?.full_name}
                      <br />
                      <small className="text-muted">{report.generated_by?.email}</small>
                    </td>
                    <td>
                      {getTimeAgo(report.created_at)}
                      <br />
                      <small className="text-muted">{formatDate(report.created_at)}</small>
                    </td>
                    <td className="actions">
                      {getActionButtons(report)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

